import React, { useEffect, useState } from 'react';
import { isTruthyAndNotEmpty } from '@stimcar/libs-kernel';
import type { StudioCapabilityId } from '../host/auth/capabilities.js';
import type { AutomationsListView, SessionAutomationView } from '../host/automations/types.js';
import * as m from '../paraglide/messages.js';
import type { SessionSummaryView } from './api.js';
import {
  createAutomation,
  deleteAutomation,
  fetchAutomations,
  listSessions,
  runAutomation,
  setAutomationEnabled,
  updateAutomation,
} from './api.js';

export type AutomationsPageProps = {
  readonly capabilities: readonly StudioCapabilityId[];
  readonly onUnauthorized: () => void;
};

export function AutomationsPage(props: AutomationsPageProps): React.ReactElement {
  const canManage = props.capabilities.includes('manage-automations');
  const [list, setList] = useState<AutomationsListView | undefined>(undefined);
  const [sessions, setSessions] = useState<readonly SessionSummaryView[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [title, setTitle] = useState('');
  const [cron, setCron] = useState('0 9 * * 1');
  const [prompt, setPrompt] = useState('');
  const [parentSessionId, setParentSessionId] = useState('');
  const [proposeMr, setProposeMr] = useState(false);
  const [busy, setBusy] = useState(false);

  async function reload(): Promise<void> {
    const [next, sessionList] = await Promise.all([fetchAutomations(), listSessions()]);
    setList(next);
    setSessions(sessionList);
    if (!isTruthyAndNotEmpty(parentSessionId) && sessionList[0] !== undefined) {
      setParentSessionId(sessionList[0].id);
    }
  }

  useEffect(() => {
    async function loadInitial(): Promise<void> {
      const [next, sessionList] = await Promise.all([fetchAutomations(), listSessions()]);
      setList(next);
      setSessions(sessionList);
      const firstSession = sessionList[0];
      if (firstSession !== undefined) {
        setParentSessionId((current) => (isTruthyAndNotEmpty(current) ? current : firstSession.id));
      }
    }
    loadInitial().then(
      () => undefined,
      (err: unknown) => {
        setError(err instanceof Error ? err.message : m.apiCallFailed());
      }
    );
  }, []);

  function resetForm(): void {
    setEditingId(undefined);
    setTitle('');
    setCron('0 9 * * 1');
    setPrompt('');
    setProposeMr(false);
    setParentSessionId(sessions[0]?.id ?? '');
  }

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!canManage) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      if (editingId !== undefined) {
        await updateAutomation(editingId, {
          title,
          cron,
          prompt,
          proposeMergeRequest: proposeMr,
        });
      } else {
        await createAutomation({
          title,
          cron,
          prompt,
          parentSessionId,
          proposeMergeRequest: proposeMr,
        });
      }
      resetForm();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : m.apiCallFailed());
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="studio-automations">
      <header className="studio-automations-header">
        <h1>{m.automationsTitle()}</h1>
        <p>{m.automationsLead()}</p>
      </header>
      {isTruthyAndNotEmpty(error) ? <p className="studio-error">{error}</p> : undefined}

      <section className="studio-automations-section">
        <h2>{m.automationsCreateHeading()}</h2>
        {canManage ? (
          <form className="studio-automations-form" onSubmit={onSubmit}>
            <label>
              {m.automationsFieldTitle()}
              <input
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                }}
                required
              />
            </label>
            <label>
              {m.automationsFieldCron()}
              <input
                value={cron}
                onChange={(event) => {
                  setCron(event.target.value);
                }}
                required
              />
            </label>
            {editingId === undefined ? (
              <label>
                {m.automationsFieldParentSession()}
                <select
                  value={parentSessionId}
                  onChange={(event) => {
                    setParentSessionId(event.target.value);
                  }}
                  required
                >
                  {sessions.length === 0 ? (
                    <option value="">{m.automationsNoParentSessions()}</option>
                  ) : undefined}
                  {sessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.title} · {session.targetLabel}
                    </option>
                  ))}
                </select>
              </label>
            ) : undefined}
            <label>
              {m.automationsFieldPrompt()}
              <textarea
                value={prompt}
                onChange={(event) => {
                  setPrompt(event.target.value);
                }}
                required
                rows={4}
              />
            </label>
            <label className="studio-automations-check">
              <input
                type="checkbox"
                checked={proposeMr}
                onChange={(event) => {
                  setProposeMr(event.target.checked);
                }}
              />
              {m.automationsFieldProposeMr()}
            </label>
            <div className="studio-automations-actions">
              <button className="button button-primary" type="submit" disabled={busy}>
                {editingId !== undefined ? m.automationsSave() : m.automationsCreate()}
              </button>
              {editingId !== undefined ? (
                <button className="button button-secondary" type="button" onClick={resetForm}>
                  {m.automationsCancelEdit()}
                </button>
              ) : undefined}
            </div>
          </form>
        ) : (
          <p>{m.automationsReadOnly()}</p>
        )}
      </section>

      <section className="studio-automations-section">
        <h2>{m.automationsGroupsHeading()}</h2>
        {(list?.groups ?? []).length === 0 ? <p>{m.automationsEmpty()}</p> : undefined}
        {(list?.groups ?? []).map((group) => (
          <div key={group.parentSessionId} className="studio-automations-session-group">
            <div className="studio-automations-session-header">
              <h3>
                {group.parentSessionTitle}
                <span className="studio-automations-meta"> · {group.parentTargetLabel}</span>
              </h3>
              <a href={group.parentSessionPath}>{m.automationsOpenSession()}</a>
            </div>
            <ul className="studio-automations-list">
              {group.automations.map((item) => (
                <AutomationRow
                  key={item.id}
                  item={item}
                  canManage={canManage}
                  onEdit={() => {
                    setEditingId(item.id);
                    setTitle(item.title);
                    setCron(item.cron);
                    setPrompt(item.prompt);
                    setParentSessionId(item.parentSessionId);
                    setProposeMr(item.proposeMergeRequest);
                  }}
                  onChanged={reload}
                  onError={setError}
                />
              ))}
            </ul>
          </div>
        ))}
      </section>
    </main>
  );
}

type AutomationRowProps = {
  readonly item: SessionAutomationView;
  readonly canManage: boolean;
  readonly onEdit: () => void;
  readonly onChanged: () => Promise<void>;
  readonly onError: (message: string) => void;
};

function AutomationRow(props: AutomationRowProps): React.ReactElement {
  const { item, canManage } = props;
  return (
    <li className="studio-automations-item">
      <div>
        <strong>{item.title}</strong>
        <p className="studio-automations-meta">
          {item.cron} · {item.parentTargetLabel} ·{' '}
          {item.enabled ? m.automationsEnabled() : m.automationsDisabled()}
        </p>
        <p className="studio-automations-prompt">{item.prompt}</p>
        <LatestRun summary={item.latestRun?.summary} status={item.latestRun?.status} />
      </div>
      {canManage ? (
        <div className="studio-automations-actions">
          <button type="button" className="button button-secondary" onClick={props.onEdit}>
            {m.automationsEdit()}
          </button>
          <button
            type="button"
            className="button button-secondary"
            onClick={() => {
              setAutomationEnabled(item.id, !item.enabled).then(props.onChanged, (err: unknown) => {
                props.onError(err instanceof Error ? err.message : m.apiCallFailed());
              });
            }}
          >
            {item.enabled ? m.automationsDisable() : m.automationsEnable()}
          </button>
          <button
            type="button"
            className="button button-secondary"
            onClick={() => {
              runAutomation(item.id).then(props.onChanged, (err: unknown) => {
                props.onError(err instanceof Error ? err.message : m.apiCallFailed());
              });
            }}
          >
            {m.automationsRunNow()}
          </button>
          <button
            type="button"
            className="button button-secondary"
            onClick={() => {
              deleteAutomation(item.id).then(props.onChanged, (err: unknown) => {
                props.onError(err instanceof Error ? err.message : m.apiCallFailed());
              });
            }}
          >
            {m.automationsDelete()}
          </button>
        </div>
      ) : undefined}
    </li>
  );
}

type LatestRunProps = {
  readonly summary: string | undefined;
  readonly status: string | undefined;
};

function LatestRun(props: LatestRunProps): React.ReactElement | undefined {
  if (props.summary === undefined) {
    return undefined;
  }
  return (
    <p className="studio-automations-run">
      {m.automationsLatestRun({ status: props.status ?? '—', summary: props.summary })}
    </p>
  );
}
